import { useState } from "react";

interface StarRatingProps {
  value: number | null;
  onChange?: (value: number) => void;
  readOnly?: boolean;
  size?: 'sm' | 'md' | 'lg';
  fractional?: boolean;
}

const SIZE_MAP = {
  sm: 14,
  md: 20,
  lg: 28,
};

export default function StarRating({
  value,
  onChange,
  readOnly = false,
  size = 'md',
  fractional = false,
}: StarRatingProps) {
  const [hovered, setHovered] = useState<number | null>(null);
  const px = SIZE_MAP[size];

  const active = hovered ?? value ?? 0;

  if (readOnly && fractional && value != null) {
    return (
      <div className="flex items-center gap-0.5" data-testid="star-rating-fractional">
        {[1, 2, 3, 4, 5].map((star) => {
          const fill = Math.min(Math.max(value - (star - 1), 0), 1);
          const id = `star-clip-${star}-${px}`;
          return (
            <svg key={star} width={px} height={px} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <clipPath id={id}>
                  <rect x="0" y="0" width={24 * fill} height="24" />
                </clipPath>
              </defs>
              <path
                d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                fill="#d1d5db"
              />
              <path
                d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                fill="#f97316"
                clipPath={`url(#${id})`}
              />
            </svg>
          );
        })}
      </div>
    );
  }

  if (readOnly) {
    return (
      <div className="flex items-center gap-0.5" data-testid="star-rating-readonly">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg key={star} width={px} height={px} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
              fill={star <= (value ?? 0) ? '#f97316' : '#d1d5db'}
            />
          </svg>
        ))}
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-0.5"
      onMouseLeave={() => setHovered(null)}
      data-testid="star-rating-interactive"
    >
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onMouseEnter={() => setHovered(star)}
          onClick={() => onChange?.(star)}
          className="focus:outline-none"
          data-testid={`star-${star}`}
        >
          <svg width={px} height={px} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
              fill={star <= active ? '#f97316' : '#d1d5db'}
              className="transition-colors"
            />
          </svg>
        </button>
      ))}
    </div>
  );
}
